import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// A span of overhead line: two wooden poles with crossarms and three conductors
// sagging between them in a shallow catenary. The piece you tile along a road to
// carry power from A to B. Each sagging conductor is two struts meeting at a low
// midpoint.
const WOOD = "#6e4f2e";
const ARM = "#5a4128";
const INSUL = "#9aa6ad";
const CABLE = "#23232a";

function strut(a: Vec3, b: Vec3, thick: number, color: string): Part {
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
  const len = Math.hypot(dx, dy, dz) || 1e-4;
  const mid: Vec3 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
  const pitch = Math.acos(Math.max(-1, Math.min(1, dy / len)));
  const yaw = Math.atan2(dx, dz);
  return { geometry: new THREE.BoxGeometry(thick, len, thick), color, position: mid, rotation: [pitch, yaw, 0], eulerOrder: "YXZ" };
}

function pole(x: number): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.05, 0.06, 1.7, 10), color: WOOD, position: [x, 0.85, 0] },
    { geometry: new THREE.BoxGeometry(0.06, 0.05, 0.7), color: ARM, position: [x, 1.54, 0] },
    { geometry: new THREE.CylinderGeometry(0.025, 0.03, 0.07, 8), color: INSUL, position: [x, 1.6, -0.3] },
    { geometry: new THREE.CylinderGeometry(0.025, 0.03, 0.07, 8), color: INSUL, position: [x, 1.6, 0] },
    { geometry: new THREE.CylinderGeometry(0.025, 0.03, 0.07, 8), color: INSUL, position: [x, 1.6, 0.3] },
  ];
}

export const powerLineSpan = defineModel({
  id: "power-line-span",
  batchId: "electric",
  name: "Power line span",
  tags: ["building", "electric", "power", "grid", "distribution", "cable", "line"],
  summary: "Two poles carrying three sagging conductors between them.",
  footprint: 1.0,
  parts(): Part[] {
    const x1 = -0.95, x2 = 0.95, y = 1.64, sag = 0.22;
    const parts: Part[] = [...pole(x1), ...pole(x2)];
    for (const z of [-0.3, 0, 0.3]) {
      const lo: Vec3 = [0, y - sag, z];
      parts.push(strut([x1, y, z], lo, 0.014, CABLE));
      parts.push(strut([x2, y, z], lo, 0.014, CABLE));
    }
    return parts;
  },
  animations: [],
});
